import { useAuth0 } from '@auth0/auth0-react'
import { GatsbyLinkProps } from 'gatsby'
import React, {
  ButtonHTMLAttributes,
  forwardRef,
  HTMLAttributes,
  ImgHTMLAttributes,
  SVGAttributes,
} from 'react'

import { useHeaderStyle } from '../../../lib/header/style'
import { cn } from '../../utils/cn'
import LinkImport from '../Link'

import DFHLogoDark from '../../../img/logos/dfh-logo-and-text.svg'
import N1Tag from '../../../img/logos/n1-tag.svg'

export const Header = forwardRef<HTMLElement, HTMLAttributes<HTMLHeadingElement>>(
  function HeaderComponent({ children, className, ...props }, ref) {
    const {
      headerStyle,
      classes: { backgroundColor },
    } = useHeaderStyle()
    return (
      <header
        ref={ref}
        {...props}
        className={cn(
          'relative border-b',
          'transition-all duration-300 ease-in-out',
          backgroundColor,
          headerStyle === 'white' ? 'border-brand-rule-grey' : 'border-transparent',
          className,
        )}
      >
        {children}
      </header>
    )
  },
)

export const List: React.FC<HTMLAttributes<HTMLUListElement>> = ({ className, children }) => {
  return <ul className={cn('flex flex-col gap-2', className)}>{children}</ul>
}

export const Link: React.FC<{
  to: GatsbyLinkProps<Record<string, unknown>>['to'] | undefined
  className?: string
}> = ({ children, className, to, ...props }) => {
  return (
    <LinkImport
      {...props}
      to={to}
      className={cn(
        'flex flex-row items-center w-fit font-gotham text-2xl md:text-sm lg:text-base font-normal md:font-medium no-underline mr-5 md:pt-[6px] border-b-[3px] border-transparent hover:border-b-brand-navy mix-blend-color hover:cursor-pointer hover:font-bold',
        className,
      )}
    >
      {children}
    </LinkImport>
  )
}

export const LogoutLink = forwardRef<
  HTMLButtonElement,
  {
    className?: ButtonHTMLAttributes<HTMLButtonElement>['className']
  }
>(function LogoutLinkComponent({ className }, ref) {
  const { logout } = useAuth0()

  return (
    <button
      ref={ref}
      onClick={() => {
        logout({
          returnTo: window.location.origin,
        })
      }}
      className={cn(
        'font-gotham text-2xl md:text-base font-normal no-underline border-none bg-transparent hover:font-medium',
        className,
      )}
    >
      Log out
    </button>
  )
})

export const Icon: React.FC<ImgHTMLAttributes<HTMLImageElement>> = ({
  alt = 'Icon',
  children,
  className,
  ...props
}) => {
  return (
    <img {...props} alt={alt} className={cn('w-4 h-4', className)}>
      {children}
    </img>
  )
}

export const DFHLogo: React.FC<ImgHTMLAttributes<HTMLImageElement>> = ({ className }) => {
  return (
    <img
      src={DFHLogoDark}
      alt="Designs for Health"
      aria-label="Designs for Health"
      className={cn('h-10 md:h-[46px] lg:h-[77px]', className)}
    />
  )
}

export const N1PractitionerSupplementBrandLogo: React.FC<ImgHTMLAttributes<HTMLImageElement>> = ({
  className,
}) => {
  return (
    <img
      src={N1Tag}
      alt="#1 Practitioner Suplement Brand"
      className={cn('h-10 md:h-[46px] lg:h-[77px]', className)}
    />
  )
}

export const MenuIcon: React.FC<SVGAttributes<SVGElement> & { isMenuOpen: boolean }> = ({
  isMenuOpen,
}) => {
  const color = isMenuOpen ? '#E64B37' : '#0b2240'

  return (
    <svg width="28" height="15" viewBox="0 0 28 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 1.5L26 1.5" stroke={color} strokeWidth="3" strokeLinecap="round" />
      <path d="M2 7.5L26 7.5" stroke={color} strokeWidth="3" strokeLinecap="round" />
      <path d="M2 13.5L26 13.5" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        {...props}
        className={cn(
          'flex gap-[3px] items-center font-gotham text-2xl md:text-sm lg:text-base font-normal hover:font-medium md:font-medium',
          className,
        )}
      >
        {children}
      </button>
    )
  },
)

Button.displayName = 'Button'
